import mapValues from 'lodash.mapvalues';
import isString from 'lodash.isstring';
import { FormControl, FormGroup, ValidatorFn } from '@angular/forms';

import { IAdvancedFormField } from '../models/advanced-form-fields.model';
import { IFieldValidationError, IValidationRules } from '../models/advanced-form-validation.model';
import {
  IAdvancedFormData,
  IAdvancedFormEvent,
  IAdvancedFormSection,
  IFieldsExtraOptions,
  IFormConfig,
  IFormData,
  IInitialFormData,
} from '../models/advanced-form.model';
import { COUNTRY_WISE_DISABLED_FIELDS_LIST } from '~shared/modules/advanced-form/constants/advanced-form.constant';

export namespace AdvancedFormUtil {
  export function validateForm(form: FormGroup): void {
    Object.values(form.controls).forEach((control) => {
      if (!control.pending) {
        control.markAsTouched();
        control.updateValueAndValidity();
      }
    });
  }

  export function generateFormConfig(
    fieldList: IAdvancedFormField[],
    validationRules: IValidationRules,
    initialFormData: IInitialFormData,
    updateOn: string
  ): IFormConfig {
    return fieldList.reduce((formConfig: IFormConfig, field: IAdvancedFormField) => {
      const fieldState = {
        disabled: field.isDisabled,
        value: initialFormData[field.name] || '',
      };

      const formControl =
        field.type === 'checkbox' && updateOn === 'blur'
          ? new FormControl(fieldState, {
              updateOn: 'change',
              validators: toValidatorsList(validationRules[field.name]),
            })
          : new FormControl(fieldState, toValidatorsList(validationRules[field.name]));

      return {
        ...formConfig,
        [field.name]: formControl,
      };
    }, {});
  }

  export function generateFormEvent(formGroup: FormGroup, eventType: string, callback): IAdvancedFormEvent {
    const payload = {
      formData: replaceEmptyFieldsToNull(formGroup.value),
      isDirty: formGroup.dirty,
    };

    return { type: eventType, payload, done: callback };
  }

  export function updateFormSectionList(
    formSectionList: IAdvancedFormSection[],
    fieldsExtraOptions: IFieldsExtraOptions,
    validationRules: IValidationRules
  ): IAdvancedFormSection[] {
    return formSectionList.map((formSection) => {
      return {
        ...formSection,
        fieldList: formSection.fieldList.map((field) => {
          const updatedFieldSettings = fieldsExtraOptions[field.name] || {};
          const fieldValidationRules = validationRules[field.name] || {};

          return {
            hasRequiredMark: Boolean(fieldValidationRules.required),
            ...field,
            ...updatedFieldSettings,
          };
        }),
      };
    });
  }

  export function getFirstValidationError(fieldName: string, errors: { [key: string]: any }): IFieldValidationError {
    const errorList = Object.keys(errors);
    const priorityErrorName = errorList[0];

    return {
      fieldName,
      errorName: priorityErrorName,
      payload: errors[priorityErrorName],
      isAsync: 'async' in errors,
    };
  }

  function toValidatorsList(validationRules: { [key: string]: any }): ValidatorFn[] {
    if (!validationRules) {
      return [];
    }

    return Object.keys(validationRules)
      .map((validationRuleName) => validationRules[validationRuleName])
      .filter(Boolean);
  }

  function replaceEmptyFieldsToNull(formData: IFormData): IAdvancedFormData {
    return mapValues(formData, (fieldValue) => (isString(fieldValue) ? fieldValue || null : fieldValue));
  }

  export function checkWhetherFieldIsPrefilledFormControl(fieldName, market) {
    return COUNTRY_WISE_DISABLED_FIELDS_LIST[market].indexOf(fieldName) >= 0;
  }

  export function isElementOutOfViewport(element) {
    const elementPosition = element.getBoundingClientRect();

    return (
      elementPosition.bottom < 0 ||
      elementPosition.right < 0 ||
      elementPosition.left > window.innerWidth ||
      elementPosition.top > window.innerHeight
    );
  }
}
